import { breadcrumbJsonLd } from 'utils/breadcrumb'
import {
    SITE,
    ORGANIZATION_ID,
    WEBSITE_ID,
    localBusinessNode,
    buildServiceNode,
} from 'utils/local-business-schema'
import { buildOfferCatalog, priceRangeLabel } from 'utils/handyman-price-data'

/**
 * JSON-LD @graph for the service hubs: the handyman, electrician, plumber and
 * part-time helper hubs, the handyman price pages and the per-job pages.
 *
 * Before this, each page hand-wrote a single-line template literal with its own
 * copy of the offers. That caused several problems:
 *  - The electrician hub hard-coded 12 prices that could drift from the price API.
 *  - The handyman hub advertised "S$50 - S$60".
 *  - The handyman hub published the aircon page's VideoObject under the aircon
 *    @id, while its WebPage pointed at a #video node that did not exist.
 *
 * Offers here are generated from the same price list the page renders, so what
 * the markup claims and what the visitor sees cannot disagree. A page passes
 * `video` only when it actually embeds that video.
 *
 * There is no aggregateRating either; see utils/local-business-schema.js for why.
 * FAQPage markup is emitted separately by components/commons/Faq.js from the
 * FAQ set the page renders.
 *
 * @param {object} opts
 * @param {string} opts.pageUrl       canonical URL, no trailing slash
 * @param {string} opts.pageName      the <title>
 * @param {string} opts.description   the meta description
 * @param {{name: string, url: string}[]} opts.trail  from buildBreadcrumb()
 * @param {object[]} opts.items       price-list items this page offers
 * @param {string} opts.serviceName
 * @param {string} opts.serviceType
 * @param {string} [opts.image]
 * @param {string} opts.datePublished
 * @param {string} opts.modifiedDate
 * @param {object} [opts.video]       { name, description, youtubeId, thumbnailUrl, uploadDate, duration }
 */
export const buildServiceHubGraph = ({
    pageUrl,
    pageName,
    description,
    trail,
    items,
    serviceName,
    serviceType,
    image,
    datePublished,
    modifiedDate,
    video,
}) => {
    const service = buildServiceNode({
        pageUrl,
        name: serviceName,
        description,
        serviceType,
        priceRange: priceRangeLabel(items),
        image,
    })
    if (service.offers && items?.length) service.offers.offerCount = String(items.length)
    if (items?.length) service.hasOfferCatalog = buildOfferCatalog(items, `${serviceName} Prices`)

    const webPage = {
        '@type': 'WebPage',
        '@id': `${pageUrl}/#webpage`,
        url: pageUrl,
        name: pageName,
        description,
        inLanguage: 'en-SG',
        isPartOf: { '@id': WEBSITE_ID },
        breadcrumb: { '@id': `${pageUrl}/#breadcrumblist` },
        mainEntity: { '@id': `${pageUrl}/#service` },
        datePublished,
        dateModified: modifiedDate,
    }
    if (image) webPage.primaryImageOfPage = { '@type': 'ImageObject', url: image }

    const videoNode = video && {
        '@type': 'VideoObject',
        '@id': `${pageUrl}/#video`,
        name: video.name,
        description: video.description,
        thumbnailUrl: [video.thumbnailUrl],
        uploadDate: video.uploadDate,
        duration: video.duration,
        embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
    }
    if (videoNode) webPage.video = { '@id': videoNode['@id'] }

    return {
        '@context': 'https://schema.org',
        '@graph': [
            breadcrumbJsonLd(trail, pageUrl),
            localBusinessNode,
            service,
            webPage,
            {
                '@type': 'Organization',
                '@id': ORGANIZATION_ID,
                name: 'StringsSG Singapore',
                url: `${SITE}/`,
            },
            {
                '@type': 'WebSite',
                '@id': WEBSITE_ID,
                url: `${SITE}/`,
                name: 'StringsSG Singapore',
                inLanguage: 'en-US',
                publisher: { '@id': ORGANIZATION_ID },
            },
            ...(videoNode ? [videoNode] : []),
        ],
    }
}
